import { Box } from "@otrium/core";
import { FavoriteBrandsWrapper, SearchWrapper } from "../Filter/Filter.styled";
import { InputTextSearch } from "../InputTextSearch";
import { Trans } from "@lingui/macro";
import {
  FilterContentWrapper,
  ResultsButton,
  ScrollWrapper,
} from "src/organisms/AlgoliaProductList/DesktopFilterVeiw/DesktopFilter.styled";
import { LabelNavigation } from "../LabelNavigation";
import { MessageDescriptor } from "@lingui/core";
import classNames from "classnames";
import {
  MobileClearAllButton,
  MobileFilterContainer,
} from "./MobileFilter.styled";
import { ArrowLeft } from "src/organisms/AlgoliaProductList/MobileFilterView/DrawerFilter/DrawerFilter.styled";
import { useRefinementList, useStats } from "react-instantsearch-core-v7";
import { sortByCount } from "src/utils/product-listing-helpers";
import { useWishListBrands } from "src/hooks/useWishListBrands";
import { useCallback, useEffect, useMemo } from "react";
import { Checkbox } from "src/atoms/Checkbox";
import { useHandleSearchParams } from "src/hooks/use-handle-search-params";

type MobileBrandFilterProps = {
  title: MessageDescriptor;
  showSearch: boolean;
  isOpen: boolean;
  attribute: string;
  toggleOpen: () => void;
  searchPlaceholder: string;
  isMultiColumn?: boolean;
  hasFilters: boolean;
  setCanRefine: (value: boolean) => void;
  onClear: () => void;
  handleScrollToTop: () => void;
};

function MobileBrandFilter({
  title,
  showSearch,
  isOpen,
  attribute,
  searchPlaceholder,
  isMultiColumn,
  hasFilters,
  toggleOpen,
  setCanRefine,
  onClear,
  handleScrollToTop,
}: MobileBrandFilterProps): JSX.Element {
  const { items, refine, searchForItems, canRefine } = useRefinementList({
    attribute,
    sortBy: sortByCount,
    limit: 200,
  });
  const { nbHits: total } = useStats();
  const { handleSearchParams } = useHandleSearchParams();

  const { data: favBrandsData } = useWishListBrands(9999, 0);
  const favoriteBrands = useMemo(() => {
    const favoriteBrandIds =
      favBrandsData?.wishlistBrands?.items.map((item) => item?.id) || [];

    return items
      .filter((item) => {
        const itemId = item.value.split("||")[0];

        return favoriteBrandIds?.includes(itemId);
      })
      .map((item) => item.value);
  }, [favBrandsData, items]);
  const favoriteBrandsSelected = useMemo(
    () =>
      items
        .filter((item) => favoriteBrands.includes(item.value))
        .every((item) => item.isRefined),
    [favoriteBrands, items]
  );

  const favoriteBrandsSlug = useMemo(() => {
    return favoriteBrands.map((brand) => {
      const splitBrand = brand.split("||");
      return splitBrand.at(-1) || "";
    });
  }, [favoriteBrands]);

  const toggleFavoriteBrands = useCallback(() => {
    handleSearchParams({
      facet: attribute,
      value: "",
      values: favoriteBrandsSlug,
      deletePage: true,
    });

    favoriteBrands.forEach((item) => {
      if (!item) {
        return;
      }

      refine(item);
    });

    handleScrollToTop();
  }, [
    attribute,
    favoriteBrands,
    favoriteBrandsSlug,
    refine,
    handleScrollToTop,
    handleSearchParams,
  ]);

  const handleRefine = useCallback(
    (value: string) => {
      handleSearchParams({ facet: attribute, value, deletePage: true });

      refine(value);

      handleScrollToTop();
    },
    [attribute, refine, handleSearchParams, handleScrollToTop]
  );

  useEffect(() => {
    setCanRefine(canRefine);
  }, [canRefine, setCanRefine]);

  return (
    <MobileFilterContainer className={classNames({ ["open"]: isOpen })}>
      <LabelNavigation
        label={title}
        leftAdornment={
          <MobileClearAllButton className="no-padding" onClick={toggleOpen}>
            <ArrowLeft strokeWidth="2" />
          </MobileClearAllButton>
        }
        rightAdornment={
          hasFilters ? (
            <MobileClearAllButton onClick={onClear}>
              <Trans>Clear all</Trans>
            </MobileClearAllButton>
          ) : undefined
        }
      />
      <div className="content-wrapper">
        <SearchWrapper>
          {showSearch ? (
            <Box>
              <InputTextSearch
                placeholder={searchPlaceholder}
                handleChange={(value) =>
                  searchForItems(value.currentTarget.value)
                }
                onClearSearch={() => searchForItems("")}
              />
            </Box>
          ) : null}

          {favoriteBrands?.length ? (
            <FavoriteBrandsWrapper>
              <Checkbox
                name="Select my favourite brands"
                checked={favoriteBrandsSelected}
                onChange={toggleFavoriteBrands}
              >
                <span>
                  <Trans>Select my favourite brands</Trans>
                </span>
              </Checkbox>
            </FavoriteBrandsWrapper>
          ) : null}
        </SearchWrapper>

        <ScrollWrapper>
          <FilterContentWrapper
            as="ul"
            isMultiColumn={isMultiColumn && items.length > 1}
            count={Math.round(items.length / 2)}
          >
            {items.map((item) => {
              const splitLabel = item.label.split("||");
              const id = splitLabel[0];
              const label = splitLabel[1];

              return (
                <Box as="li" key={`${attribute}-${id}`}>
                  <Checkbox
                    name={label}
                    checked={item.isRefined}
                    onChange={() => handleRefine(item.value)}
                  >
                    {label}
                  </Checkbox>
                </Box>
              );
            })}
          </FilterContentWrapper>
        </ScrollWrapper>

        <ResultsButton
          fullWidth
          variant="primary"
          onClick={toggleOpen}
          disabled={total <= 0}
          className="total-button"
        >
          <span>
            {total > 0 ? (
              <Trans>Show {total} results</Trans>
            ) : (
              <Trans>Close</Trans>
            )}
          </span>
        </ResultsButton>
      </div>
    </MobileFilterContainer>
  );
}

export { MobileBrandFilter };
